import { OBLIGATION_CONSENSUS_FIELD_TO_QUESTION_ID } from '@/shared/constants/obligationProposalQuestionMap';

export const OBLIGATION_PROPOSAL_UPDATED_TYPE = 'OBLIGATION_PROPOSAL_UPDATED';

const FIELD_KEY_BY_QUESTION_ID = Object.fromEntries(
  Object.entries(OBLIGATION_CONSENSUS_FIELD_TO_QUESTION_ID).map(([fieldKey, questionId]) => [
    String(questionId),
    fieldKey,
  ]),
);

const FORM_TAB_BY_FIELD_KEY = Object.freeze({
  problem_definition: 'problem',
  problem_causes: 'problem',
  obligation_previous_work: 'description',
  obligation_proposed_solution: 'description',
  obligation_expected_results: 'description',
  analysis_transparency: 'analysis',
  analysis_accountability: 'analysis',
  analysis_participation: 'analysis',
});

/**
 * @param {string|number|null|undefined} questionId
 * @returns {string|null}
 */
export function fieldKeyForQuestionId(questionId) {
  if (questionId == null || questionId === '') return null;
  return FIELD_KEY_BY_QUESTION_ID[String(questionId)] || null;
}

/**
 * @param {string|number|null|undefined} questionId
 * @returns {string|null}
 */
export function formTabForQuestionId(questionId) {
  const fieldKey = fieldKeyForQuestionId(questionId);
  return fieldKey ? FORM_TAB_BY_FIELD_KEY[fieldKey] || null : null;
}

/**
 * @param {string|null|undefined} fieldKey
 * @returns {string|null}
 */
export function formTabForFieldKey(fieldKey) {
  if (!fieldKey) return null;
  return FORM_TAB_BY_FIELD_KEY[fieldKey] || null;
}

/**
 * @param {object[]} proposals
 * @param {string|number} proposalId
 * @returns {{ obligation_question_id: number|null, fieldKey: string|null, formTab: string|null }}
 */
export function resolveProposalMetaFromSearchRows(proposals = [], proposalId) {
  const idStr = String(proposalId);
  const match = (proposals || []).find((row) => {
    const rowId = String(row?.id ?? row?.obligation_proposal_id ?? '');
    return rowId === idStr;
  });
  const questionId = match?.obligation_question_id ?? null;
  const fieldKey = fieldKeyForQuestionId(questionId);
  return {
    obligation_question_id: questionId != null ? Number(questionId) : null,
    fieldKey,
    formTab: formTabForFieldKey(fieldKey),
  };
}

/**
 * @param {object} params
 * @param {string|number} params.obligationId
 * @param {string|number|null|undefined} params.actionPlanId
 * @param {string|number|null|undefined} params.proposalId
 * @param {string|null|undefined} params.formTab
 * @param {string|null|undefined} params.fieldKey
 * @returns {string}
 */
export function buildProfileObligationProposalFocusHref({
  obligationId,
  actionPlanId,
  proposalId,
  formTab,
  fieldKey,
}) {
  const params = new URLSearchParams({ tab: 'obaveze' });
  if (actionPlanId) params.set('actionPlanId', String(actionPlanId));
  if (formTab) params.set('formTab', formTab);
  if (fieldKey) params.set('focusField', fieldKey);
  if (proposalId) params.set('focusProposalId', String(proposalId));
  return `/profil/predlog-obaveze/${obligationId}?${params.toString()}`;
}

/**
 * Intermedijarna ruta — resolve proposal_id → obligation_id pa redirect.
 * @param {string|number} proposalId
 * @param {{ actionPlanId?: string|null, formTab?: string|null, fieldKey?: string|null }} [query]
 * @returns {string}
 */
export function buildProfileObligationProposalOpenHref(proposalId, query = {}) {
  const params = new URLSearchParams({ tab: 'obaveze' });
  if (query.actionPlanId) params.set('actionPlanId', String(query.actionPlanId));
  if (query.formTab) params.set('formTab', query.formTab);
  if (query.fieldKey) params.set('focusField', query.fieldKey);
  if (proposalId) params.set('focusProposalId', String(proposalId));
  return `/profil/predlog-obaveze/otvori/${proposalId}?${params.toString()}`;
}

/**
 * @param {object[]} notifications
 * @returns {string[]}
 */
export function collectProposalIdsFromNotifications(notifications = []) {
  return [
    ...new Set(
      (notifications || [])
        .filter((n) => (n?.notification_type || n?.type || '') === OBLIGATION_PROPOSAL_UPDATED_TYPE)
        .map((n) => n?.related_entity_id ?? n?.relatedEntityId)
        .filter((id) => id != null && id !== '')
        .map(String),
    ),
  ];
}

/**
 * @param {object|null|undefined} consensusEntry
 * @param {string|number|null|undefined} proposalId
 * @returns {boolean}
 */
export function isConsensusEntryFocusedByProposalId(consensusEntry, proposalId) {
  if (!proposalId || !consensusEntry) return false;
  const idStr = String(proposalId);
  if (String(consensusEntry?.latestProposalId ?? '') === idStr) return true;
  return (consensusEntry?.rounds || []).some(
    (round) => String(round?.proposalId ?? '') === idStr,
  );
}

/**
 * @param {object|null|undefined} formData
 * @param {string|number|null|undefined} proposalId
 * @returns {string|null}
 */
export function findFocusedFieldKeyInFormData(formData, proposalId) {
  if (!proposalId || !formData?.field_consensus) return null;
  for (const [fieldKey, entry] of Object.entries(formData.field_consensus)) {
    if (isConsensusEntryFocusedByProposalId(entry, proposalId)) return fieldKey;
  }
  return null;
}

/**
 * @param {string|null|undefined} fieldKey
 * @returns {string}
 */
export function fieldConsensusDomId(fieldKey) {
  return fieldKey ? `field-consensus-${fieldKey}` : 'field-consensus';
}
